/**
 * Import Brand-Specific BOM tab (v3 — Feb 2026).
 *
 * v3 changes over Feb 2026 v2:
 *   • RECREATE removed from the UI. Per-line Mode dropdown now offers
 *     only LINK and SKIP; any backend-proposed RECREATE line defaults
 *     to LINK (when link_allowed) or SKIP. RecreateEditor + artwork
 *     upload + asset_link input all deleted. Submit payload no longer
 *     forwards completed_category_data / asset_link / artwork_files.
 *   • Compact 2×2 selection layout:
 *         (1,1) Target Buyer SKU picker    (1,2) Source Buyer SKU search
 *         (2,1) Brand chip (read-only)     (2,2) Model chip (read-only)
 *     Source candidates render in a bounded scrollable popover anchored
 *     to the source input (~14rem height, own width), not a full-width
 *     block that pushes the page.
 *   • Model chip is populated by the buyer-sku list endpoint's new
 *     model_id / model_code / model_name enrichment; source candidate
 *     list is model-scoped by default (cross-model toggle to expand).
 */
import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, X, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Searchable combobox — same UX as DemandHub.js's PmSearchSelect.
function TargetBuyerSkuPicker({ value, onChange, options, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => (o.label || o.value || "").toLowerCase().includes(needle));
  }, [q, options]);
  const selectedLabel = options.find((o) => o.value === value)?.label || value || "";
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full h-9 px-3 text-sm text-left border rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/40"
        data-testid={testId}
      >
        {selectedLabel || <span className="text-gray-400">{placeholder}</span>}
      </button>
      {open && (
        <div className="absolute z-40 mt-1 w-full max-h-72 overflow-y-auto bg-white border rounded-md shadow-lg">
          <div className="sticky top-0 bg-white border-b p-2">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Type to search..."
              className="w-full h-8 px-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40"
              data-testid={`${testId}-search`}
            />
          </div>
          {filtered.length === 0 ? (
            <div className="p-3 text-xs text-gray-400">No matches</div>
          ) : filtered.slice(0, 200).map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); setQ(""); }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 font-mono text-xs ${opt.value === value ? "bg-primary/10 font-medium" : ""}`}
              data-testid={`${testId}-option-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const CATEGORY_COLORS = {
  LB: "bg-amber-100 text-amber-800 border-amber-200",
  BS: "bg-violet-100 text-violet-800 border-violet-200",
  PM: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const MODE_STYLES = {
  LINK: "bg-emerald-50 border-emerald-200 text-emerald-800",
  SKIP: "bg-zinc-50 border-zinc-200 text-zinc-500",
};

const SourceChip = ({ label, onRemove }) => (
  <span
    className="inline-flex items-center gap-2 px-2 py-1 border border-zinc-300 rounded-md text-xs font-mono bg-white"
    data-testid={`source-chip-${label}`}
  >
    {label}
    <button
      className="text-zinc-500 hover:text-red-600"
      onClick={onRemove}
      title="Remove source"
      data-testid={`source-chip-remove-${label}`}
    >
      <X className="w-3 h-3" />
    </button>
  </span>
);

const ModeLegend = () => (
  <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-zinc-600" data-testid="mode-legend">
    <div className="flex items-center gap-1.5">
      <Badge className={`${MODE_STYLES.LINK} text-[10px]`}>LINK</Badge>
      <span>Reuse the same RM as-is. Generic items or items already tagged to the target brand.</span>
    </div>
    <div className="flex items-center gap-1.5">
      <Badge className={`${MODE_STYLES.SKIP} text-[10px]`}>SKIP</Badge>
      <span>Don&apos;t import — target already has it or doesn&apos;t need it.</span>
    </div>
  </div>
);

// v3 (Feb 2026) — coerce any RECREATE line coming from the backend
// down to LINK (if allowed) or SKIP. Preserves backend semantics
// without exposing RECREATE to planners.
const collapseMode = (mode, link_allowed) => {
  if (mode === "RECREATE") return link_allowed ? "LINK" : "SKIP";
  return mode;
};

export default function ImportBrandBomTab({ buyerSkus = [], brands = [] }) {
  // ── Target ──
  const [targetBuyerSkuId, setTargetBuyerSkuId] = useState("");
  const targetRow = useMemo(
    () => buyerSkus.find((s) => s.buyer_sku_id === targetBuyerSkuId) || null,
    [buyerSkus, targetBuyerSkuId]
  );
  const targetBidsoSkuId = targetRow?.bidso_sku_id || "";
  const targetBrandId = targetRow?.brand_id || "";
  const targetBrand = useMemo(
    () => brands.find((b) => b.id === targetBrandId) || null,
    [brands, targetBrandId]
  );
  const targetModelId = targetRow?.model_id || "";
  const targetModelCode = targetRow?.model_code || "";
  const targetModelName = targetRow?.model_name || "";

  // ── Sources ──
  const [sources, setSources] = useState([]);
  const [sourceQuery, setSourceQuery] = useState("");
  const [sourcePickerOpen, setSourcePickerOpen] = useState(false);
  const [crossModel, setCrossModel] = useState(false);
  const sourceCellRef = useRef(null);

  // Close the source popover when clicking outside the source cell.
  useEffect(() => {
    if (!sourcePickerOpen) return undefined;
    const onDown = (e) => {
      if (sourceCellRef.current && !sourceCellRef.current.contains(e.target)) {
        setSourcePickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [sourcePickerOpen]);

  // ── Preview ──
  const [previewId, setPreviewId] = useState(null);
  const [previewLines, setPreviewLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const targetPickerOptions = useMemo(() => {
    // Aug 2026 — no client-side pre-slice. The combobox already caps
    // *rendered* results at 200 after search-filtering (see
    // TargetBuyerSkuPicker::filtered.slice(0, 200)), so a full-universe
    // map here means the search box can reach every active buyer SKU
    // instead of an arbitrary early slice of ~500 rows.
    return buyerSkus.map((s) => ({
      value: s.buyer_sku_id,
      label: `${s.buyer_sku_id}${s.brand_code ? " · " + s.brand_code : ""}${s.model_code ? " · " + s.model_code : ""}${s.name ? " — " + s.name : ""}`,
    }));
  }, [buyerSkus]);

  const sourceCandidates = useMemo(() => {
    const q = sourceQuery.trim().toLowerCase();
    const already = new Set(sources.map((s) => `${s.bidso_sku_id}::${s.brand_id}`));
    return buyerSkus
      .filter((s) => s.buyer_sku_id !== targetBuyerSkuId)
      .filter((s) => !already.has(`${s.bidso_sku_id || s.buyer_sku_id}::${s.brand_id}`))
      .filter((s) => {
        // Model filter unless cross-model toggle is on.
        if (crossModel || !targetModelId) return true;
        return s.model_id === targetModelId;
      })
      .filter((s) => {
        if (!q) return true;
        return (
          (s.buyer_sku_id || "").toLowerCase().includes(q) ||
          (s.bidso_sku_id || "").toLowerCase().includes(q) ||
          (s.brand_code || "").toLowerCase().includes(q) ||
          (s.model_code || "").toLowerCase().includes(q)
        );
      })
      .slice(0, 50);
  }, [buyerSkus, targetBuyerSkuId, targetModelId, sources, sourceQuery, crossModel]);

  // Load preview when target + at least one source is picked.
  useEffect(() => {
    if (!targetBidsoSkuId || !targetBrandId || sources.length === 0) {
      setPreviewLines([]);
      setPreviewId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.post(`${API}/demand-hub/brand-bom-import-requests`, {
          target_bidso_sku_id: targetBidsoSkuId,
          target_brand_id: targetBrandId,
          source_buyer_skus: sources.map((s) => ({
            bidso_sku_id: s.bidso_sku_id,
            brand_id: s.brand_id,
          })),
        });
        if (cancelled) return;
        const detail = await axios.get(
          `${API}/demand-hub/brand-bom-import-requests/${res.data.id}`
        );
        if (cancelled) return;
        setPreviewId(res.data.id);
        // v3 — collapse any RECREATE lines the backend proposes down to
        // LINK / SKIP. The UI does not surface RECREATE any more.
        const collapsed = (detail.data.proposed_lines || []).map((ln) => ({
          ...ln,
          mode: collapseMode(ln.mode, ln.link_allowed),
        }));
        setPreviewLines(collapsed);
      } catch (e) {
        const msg = e?.response?.data?.detail || "Preview failed";
        toast.error(msg);
        setPreviewLines([]);
        setPreviewId(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [targetBidsoSkuId, targetBrandId, sources]);

  const updateLine = (rmId, patch) => {
    setPreviewLines((prev) =>
      prev.map((ln) => (ln.source_rm_id === rmId ? { ...ln, ...patch } : ln))
    );
  };

  const submit = async () => {
    if (!previewId) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/demand-hub/brand-bom-import-requests`, {
        target_bidso_sku_id: targetBidsoSkuId,
        target_brand_id: targetBrandId,
        source_buyer_skus: sources.map((s) => ({
          bidso_sku_id: s.bidso_sku_id,
          brand_id: s.brand_id,
        })),
        // v3 — payload trimmed: LINK/SKIP only, no completed_category_data
        // / asset_link / artwork_files.
        proposed_lines: previewLines.map((ln) => ({
          source_rm_id: ln.source_rm_id,
          mode: ln.mode,
          quantity: ln.quantity,
          unit: ln.unit,
        })),
      });
      toast.success(`Import request submitted (#${res.data.id.slice(0, 8)})`);
      setTargetBuyerSkuId("");
      setSources([]);
      setPreviewLines([]);
      setPreviewId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  // v3 — every line is trivially complete (LINK/SKIP require no editor
  // input). Submit is enabled once target + brand + at least one line
  // are resolved.
  const readyToSubmit =
    !!targetBidsoSkuId && !!targetBrandId && previewLines.length > 0;

  return (
    <div className="space-y-3" data-testid="import-brand-bom-tab">
      <Card>
        <CardContent className="p-4 space-y-4">
          {/* ── Compact 2×2 selection grid (v3) ─────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* (1,1) Target Buyer SKU picker */}
            <div data-testid="target-buyer-sku-select">
              <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1.5">
                Target Buyer SKU
              </div>
              <TargetBuyerSkuPicker
                value={targetBuyerSkuId}
                onChange={setTargetBuyerSkuId}
                options={targetPickerOptions}
                placeholder="Pick target Buyer SKU…"
                testId="target-buyer-sku-select"
              />
            </div>

            {/* (1,2) Source Buyer SKU search + bounded popover */}
            <div ref={sourceCellRef} className="relative">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[11px] uppercase tracking-wide text-zinc-500">
                  Source Buyer SKU(s)
                </div>
                <label
                  className="flex items-center gap-1.5 text-[11px] text-zinc-600 cursor-pointer select-none"
                  data-testid="cross-model-toggle-label"
                >
                  <input
                    type="checkbox"
                    checked={crossModel}
                    onChange={(e) => setCrossModel(e.target.checked)}
                    data-testid="cross-model-toggle"
                    className="h-3.5 w-3.5"
                  />
                  Show all models
                </label>
              </div>
              <Input
                placeholder={targetModelCode && !crossModel
                  ? `Search (model ${targetModelCode})…`
                  : "Search buyer SKU…"}
                value={sourceQuery}
                onChange={(e) => setSourceQuery(e.target.value)}
                onFocus={() => setSourcePickerOpen(true)}
                className="h-9 text-sm"
                data-testid="source-search-input"
              />
              {sourcePickerOpen && targetBuyerSkuId && (
                <div
                  className="absolute z-30 left-0 right-0 mt-1 border rounded-md bg-white shadow-lg divide-y max-h-56 overflow-auto"
                  data-testid="source-candidates"
                >
                  {sourceCandidates.length === 0 ? (
                    <div className="p-3 text-xs text-zinc-500">
                      {crossModel || !targetModelId
                        ? "No candidate buyer SKUs."
                        : `No candidates for model ${targetModelCode}. Toggle "Show all models" to import cross-model.`}
                    </div>
                  ) : (
                    sourceCandidates.map((s) => (
                      <button
                        key={`${s.bidso_sku_id || s.buyer_sku_id}::${s.brand_id}`}
                        className="w-full text-left p-2 hover:bg-zinc-50 flex items-center gap-2"
                        onClick={() => {
                          setSources((prev) => [...prev, {
                            bidso_sku_id: s.bidso_sku_id || s.buyer_sku_id,
                            brand_id: s.brand_id,
                            brand_code: s.brand_code,
                            label: s.buyer_sku_id || s.bidso_sku_id,
                          }]);
                          setSourceQuery("");
                        }}
                        data-testid={`add-source-${s.bidso_sku_id || s.buyer_sku_id}`}
                      >
                        <Plus className="w-3 h-3" />
                        <span className="font-mono text-xs">{s.buyer_sku_id || s.bidso_sku_id}</span>
                        {s.brand_code && <span className="text-zinc-500 text-xs">· {s.brand_code}</span>}
                        {s.model_code && <span className="text-zinc-400 text-[10px]">· {s.model_code}</span>}
                      </button>
                    ))
                  )}
                </div>
              )}
              {sources.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="source-chips">
                  {sources.map((s) => (
                    <SourceChip
                      key={`${s.bidso_sku_id}::${s.brand_id}`}
                      label={`${s.label || s.bidso_sku_id} · ${s.brand_code || s.brand_id}`}
                      onRemove={() =>
                        setSources((prev) =>
                          prev.filter((x) => !(x.bidso_sku_id === s.bidso_sku_id && x.brand_id === s.brand_id))
                        )
                      }
                    />
                  ))}
                </div>
              )}
            </div>

            {/* (2,1) Brand chip (read-only) */}
            <div
              className="px-2.5 py-1.5 border border-zinc-200 rounded-md bg-zinc-50/60"
              data-testid="target-brand-display"
            >
              <div className="text-[10px] text-zinc-500 uppercase tracking-wide">Brand</div>
              {targetBrand ? (
                <div className="text-xs mt-0.5">
                  <span className="font-mono font-medium">{targetBrand.code}</span>
                  <span className="text-zinc-500 ml-1.5">· {targetBrand.name}</span>
                </div>
              ) : (
                <div className="text-[11px] text-zinc-400 mt-0.5">from Buyer SKU</div>
              )}
            </div>

            {/* (2,2) Model chip (read-only) */}
            <div
              className="px-2.5 py-1.5 border border-zinc-200 rounded-md bg-zinc-50/60"
              data-testid="target-model-display"
            >
              <div className="text-[10px] text-zinc-500 uppercase tracking-wide">Model</div>
              {targetModelCode ? (
                <div className="text-xs mt-0.5">
                  <span className="font-mono font-medium">{targetModelCode}</span>
                  {targetModelName && <span className="text-zinc-500 ml-1.5">· {targetModelName}</span>}
                </div>
              ) : (
                <div className="text-[11px] text-zinc-400 mt-0.5">from Buyer SKU</div>
              )}
            </div>
          </div>

          {/* ── Review proposed lines ─────────────────────── */}
          <div>
            <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1.5">
              Review proposed lines
            </div>
            {loading && (
              <div className="text-xs text-zinc-500 flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Resolving…
              </div>
            )}
            {!loading && previewLines.length === 0 && (
              <div className="text-xs text-zinc-500">
                Pick a target + at least one source to preview.
              </div>
            )}
            {!loading && previewLines.length > 0 && (
              <>
                <div className="overflow-auto">
                  <table className="w-full text-xs" data-testid="preview-lines-table">
                    <thead className="text-[10px] text-zinc-500">
                      <tr className="border-b">
                        <th className="text-left py-1.5 pr-2">Item</th>
                        <th className="text-left py-1.5 pr-2">Cat</th>
                        <th className="text-left py-1.5 pr-2">Source</th>
                        <th className="text-left py-1.5 pr-2">Brand</th>
                        <th className="text-right py-1.5 pr-2">Qty</th>
                        <th className="text-left py-1.5 pr-2">UOM</th>
                        <th className="text-left py-1.5 pr-2">Mode</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewLines.map((ln) => (
                        <tr key={ln.source_rm_id} className="border-b" data-testid={`line-${ln.source_rm_id}`}>
                          <td className="py-1.5 pr-2">
                            <div className="font-mono text-[11px]">{ln.source_rm_id}</div>
                            <div className="text-[10px] text-zinc-500 truncate max-w-[280px]">
                              {ln.description || ln.rm_name}
                            </div>
                          </td>
                          <td className="py-1.5 pr-2">
                            {ln.category && (
                              <Badge className={`${CATEGORY_COLORS[ln.category] || "bg-zinc-100"} text-[10px]`}>
                                {ln.category}
                              </Badge>
                            )}
                          </td>
                          <td className="py-1.5 pr-2 font-mono text-[10px] text-zinc-500">
                            {ln.source_buyer_sku?.bidso_sku_id}
                          </td>
                          <td className="py-1.5 pr-2">
                            {ln.brand_match ? (
                              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">match</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]" data-testid={`brand-mismatch-${ln.source_rm_id}`}>
                                cross-brand
                              </Badge>
                            )}
                          </td>
                          <td className="py-1.5 pr-2 text-right">
                            <Input
                              type="number"
                              className="w-16 h-7 text-right text-xs"
                              value={ln.quantity}
                              onChange={(e) => updateLine(ln.source_rm_id, { quantity: Number(e.target.value) })}
                              data-testid={`qty-${ln.source_rm_id}`}
                            />
                          </td>
                          <td className="py-1.5 pr-2">
                            <Input
                              className="w-14 h-7 text-xs"
                              value={ln.unit}
                              onChange={(e) => updateLine(ln.source_rm_id, { unit: e.target.value })}
                              data-testid={`unit-${ln.source_rm_id}`}
                            />
                          </td>
                          <td className="py-1.5 pr-2">
                            <Select
                              value={ln.mode}
                              onValueChange={(v) => updateLine(ln.source_rm_id, { mode: v })}
                            >
                              <SelectTrigger
                                className={`w-24 h-7 text-[11px] ${MODE_STYLES[ln.mode] || ""}`}
                                data-testid={`mode-${ln.source_rm_id}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="LINK" disabled={!ln.link_allowed}>
                                  LINK{!ln.link_allowed && " (blocked)"}
                                </SelectItem>
                                <SelectItem value="SKIP">SKIP</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ModeLegend />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={submit}
          disabled={!readyToSubmit || submitting}
          data-testid="submit-import-brand-bom"
        >
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
          Submit for Tech Ops review
        </Button>
      </div>
    </div>
  );
}
